import React, { useEffect, useState } from 'react';
import { Button, Text, TextInput, View } from "../../Components/Themed"
import FormattedTextInput from '../FormattedTextInput';
import InputBar from '../InputBar';
import type { OrderProps, PostProps, PublicPlayerProps, TextTagProps } from '../../types';
import { SocialComponentApi, SocialComponentHelpers } from '../api';
import ImageList from '../PostCard/components/ImageList';
import { useColors } from '../../constants/useColors';
import { Icons } from '../../Components';
import { FlatList } from 'react-native';
import SocialOrdersList from '../SocialOrdersList';

type PosterProps = {
    width:number,
    post_id?:string,
    onClose:() => void,
    onSelectImage:(image:any) => void,
    insets?:{ top:number, left:number, right:number, bottom:number },
    onCreatePost:(post:PostProps) => void
}

const sections = ['input_bar','title','post','orders','images']
const Poster = ({ width, post_id, insets, onCreatePost, onSelectImage, onClose }: PosterProps) => {
    const Colors = useColors();
    const [ submit_caller, setSubmitCaller ] = useState<string|undefined>(undefined);
    const [ post_data, setPostData ] = useState<{
        draft_post?:PostProps,
        players:PublicPlayerProps[],
        post?:PostProps,
        orders:OrderProps[],
        action_loading:boolean
    }>({
        action_loading: false,
        players:[],
        orders:[]
    });

    const { post, draft_post, action_loading, orders } = post_data;
    const valid = SocialComponentHelpers.isPostValid(draft_post);

    useEffect(() => {
        SocialComponentApi.setEnvironment();
        setUpPost(post_id);
    },[post_id]);

    const setUpPost = async(post_id?:string) => {
        let existing_post:PostProps | undefined
        let ords:OrderProps[] = []
        let playrs:PublicPlayerProps[] = []
        if(post_id){
            existing_post = await SocialComponentApi.getPostById(post_id, 'memo');
            if(existing_post){
                const set_up_data = await SocialComponentHelpers.getPostsData([existing_post]);
                ords = set_up_data.orders
                playrs = set_up_data.players
            }
            
        } //Not set up to handle this yet!
        setPostData({ 
            ...post_data, 
            orders: ords,
            players: playrs,
            draft_post: existing_post ?? SocialComponentHelpers.getEmptyPost(),
            post: existing_post
        })
    }

    const handlePublishPost = async(data:{ text:string, formatted_text:string }, tags:TextTagProps[]) => {
        if(!draft_post || action_loading || !valid){ return }
        setPostData({
            ...post_data,
            action_loading:true 
        });
        //Drop data on tags
        tags = tags.map(t => { return { ...t, data: undefined } });
        let saved_post:PostProps | undefined = undefined

        //Lets check for urls
        const url = SocialComponentHelpers.getUrlsFromString(data.text.toLowerCase());
        let memo_urls:string[] = []
        if(url){ memo_urls.push(url) }

        if(draft_post.memo_post_id){
            saved_post = await SocialComponentApi.updatePost({ ...draft_post, memo_urls, tags: tags, body: data.text, formatted_text: data.formatted_text });
        } else {
            saved_post = await SocialComponentApi.createPost({ ...draft_post, memo_urls, tags: tags, body: data.text, formatted_text: data.formatted_text });
        }
        setPostData({
            ...post_data,
            draft_post: undefined,
            action_loading:false
        });
        if(!saved_post){ return }
        onCreatePost(saved_post);
    }

    const renderSections = (data:{ item:string, index:number }) => {
        switch(data.item){
            case 'input_bar':
                return (
                    <View style={{ borderBottomWidth:1, borderColor:Colors.borders.light }}>
                        <InputBar
                            actions={['image','action']}
                            insets={insets}
                            width={width}
                            onTagOrder={(order) => {
                                if(!draft_post){ return }
                                let exists = draft_post.memo_orders.find(o => o == order.order_id);
                                if(exists){
                                    setPostData({
                                        ...post_data,
                                        orders: orders.filter(o => o.order_id != order.order_id),
                                        draft_post: {
                                            ...draft_post,
                                            memo_orders: draft_post.memo_orders.filter(o => o != order.order_id)
                                        }
                                    })
                                } else {
                                    //Lets check if its already included!
                                    setPostData({
                                        ...post_data,
                                        orders: orders.filter(o => o.order_id != order.order_id).concat(order),
                                        draft_post: {
                                            ...draft_post,
                                            memo_orders: draft_post.memo_orders.filter(o => o != order.order_id).concat(order.order_id)
                                        }
                                    })
                                }
                            }}
                            onImageUpload={(image) => {
                                if(!draft_post){ return }
                                setPostData({
                                    ...post_data,
                                    draft_post: { 
                                        ...draft_post,
                                        memo_images: draft_post.memo_images.concat(image)
                                    }
                                })
                            }}
                        />
                    </View>
                )
            case 'title':
                if(!draft_post){ return <></> }
                return (
                    <View nativeID='title'>
                        <TextInput
                            style={{ borderWidth:0, fontSize:14 }}
                            placeholder='Title'
                            value={draft_post.title}
                            weight='bold'
                            onChangeText={(text) => {
                                if(!draft_post){ return }
                                setPostData({ 
                                    ...post_data,
                                    draft_post: {
                                        ...draft_post,
                                        title: text
                                    }
        
                                })
                            }}
                        />
                    </View>
                )
            case 'post':
                if(!draft_post){ return <></> }
                return (
                <View nativeID='post_body'>
                    <FormattedTextInput
                        hide_border
                        width={width}
                        disable_scroll
                        tag_selector_location='bottom'
                        submit_caller={submit_caller}
                        init_data={{ text: post?.body ?? '', formatted_text: post?.formatted_text ?? '', tags: post?.tags ?? [] }}
                        placeholder='Start typing here ...'
                        onSubmit={(data, tags) => handlePublishPost(data, tags)}
                        onChangeText={(data) => {
                            if(!draft_post){ return }
                            setPostData({ 
                                ...post_data,
                                draft_post: {
                                    ...draft_post,
                                    body: data.text,
                                    formatted_text: data.formatted_text
                                }

                            })
                        }}
                    />
                </View>
                )
            case 'orders':
                if(!draft_post){ return <></> }
                if(!draft_post.memo_orders){ return <></> }
                return (
                    <View style={{ padding:10 }}>
                        <SocialOrdersList
                            orders={orders.filter(o => draft_post.memo_orders.includes(o.order_id))}
                            players={[]}
                            onUntagOrder={(order) => {
                                if(!draft_post){ return }
                                setPostData({
                                    ...post_data,
                                    orders: orders.filter(o => o.order_id != order.order_id),
                                    draft_post: {
                                        ...draft_post,
                                        memo_orders: draft_post.memo_orders.filter(o => o != order.order_id)
                                    }
                                })
                            }}
                            maxWidth={width}
                            direction='horizontal'
                        />
                    </View>
                )
            case 'images':
                if(!draft_post){ return <></> }
                if(!draft_post.memo_images){ return <></> }
                return (
                    <View>
                        <ImageList
                            images={draft_post.memo_images}
                            width={width-10}
                            post_id=''
                            onSelectImage={onSelectImage}
                        />
                    </View>
                )

            default: return <></>
        }
    }


    if(!draft_post){ return <></> }

    return (
        <View style={{ flex:1 }}>
            <View nativeID='actions' type='header' style={{ flexDirection:'row', alignItems:'center', padding:10, paddingTop:(insets?.top ?? 0) + 10 }}>
                <Button transparent style={{ flexDirection:'row', alignItems:'center' }} onPress={() => onClose()}>
                    <Icons.CloseIcon  size={10} color={Colors.text.h1} />
                    <Text theme='description' style={{ marginLeft:10 }}>Close</Text>
                </Button>
                <View transparent style={{ flex:1 }} />
                <Button
                    title='Publish'
                    type='success'
                    disabled={!valid}
                    loading={action_loading}
                    style={{ padding:10, opacity: valid && !action_loading ? 1 : 0.5 }}
                    onPress={() => setSubmitCaller(Math.random().toString())}
                />
            </View>
            <FlatList
                data={sections}
                key={`poster_sections`}
                keyboardShouldPersistTaps
                renderItem={renderSections}
                keyExtractor={item => item}
            />
            
        </View>
    )
}

export default Poster